import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import type { InvoicePrefSubscriber } from "../../api/invoicePrefs";
import { disableSubscriber, disableSummary, type DisableResult } from "../../api/subscriberDisable";

/**
 * Disable subscriber (owner 2026-10-14). Asks once, then stops sign-in. The
 * records are kept 2 years and the history records who did it, so the
 * wording says "disable", never "delete".
 */
export function SubscriberDisableDialog({
  ns,
  subscriber,
  onClose,
}: {
  ns: string;
  subscriber: InvoicePrefSubscriber | null;
  onClose: () => void;
}) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [done, setDone] = useState<DisableResult | null>(null);

  const mutation = useMutation({
    mutationFn: () => disableSubscriber(identity, ns, subscriber?.accountid ?? ""),
    onSuccess: async (r) => {
      setDone(r);
      await qc.invalidateQueries({ queryKey: ["invoiceprefs", ns] });
      await qc.invalidateQueries({ queryKey: ["rechargeoptions", ns] });
    },
  });

  const close = () => {
    setDone(null);
    mutation.reset();
    onClose();
  };

  const who = subscriber ? subscriber.name || subscriber.username || subscriber.accountid : "";
  const err = mutation.error;

  return (
    <Dialog open={subscriber !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Disable {who}</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success">{disableSummary(done)}</Alert>
        ) : (
          <>
            <Typography sx={{ mb: 2 }}>
              {subscriber?.mobile ? `${subscriber.mobile} · ` : ""}
              {subscriber?.username}
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This subscriber and their devices stop signing in straight away. Their sessions, payments and history are
              kept for 2 years, and the record shows that you disabled them. The same mobile can be registered again
              later.
            </Alert>
          </>
        )}
        {err && <Alert severity="error">{err instanceof Error ? err.message : String(err)}</Alert>}
      </DialogContent>
      <DialogActions>
        {done ? (
          <Button variant="contained" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button color="error" variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending ? "Disabling…" : "Disable"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
